import { useQuery } from '@tanstack/react-query';
import { cropTypesService } from '../services/cropTypesService';
import { cropSubTypesService } from '../services/cropSubTypesService';
import { regionService } from '../services/normsService';
import { elementRankingService } from '../services/elementRankingService';
import { labsService } from '../services/labsService';

/**
 * Client-side caching for small, slow-changing reference/lookup lists
 * (crop types, crop sub-types, regions, element rankings, labs).
 *
 * These lists were previously fetched with raw `service.getAll()` + `useState`
 * inside each screen, so every mount hit the API again. Routing them through
 * react-query gives a shared, deduplicated cache: the first screen to need a
 * list fetches it, and every subsequent consumer reads it from cache until it
 * goes stale.
 *
 * All hooks share {@link REFERENCE_DATA_STALE_TIME} and a stable `queryKey`
 * (see {@link referenceQueryKeys}) so identical requests dedupe and cache.
 */

/**
 * How long a reference list stays "fresh" before react-query will refetch it on
 * the next mount. Reference data changes rarely, so 5 minutes avoids a refetch
 * on every screen navigation while still picking up edits within a session.
 */
export const REFERENCE_DATA_STALE_TIME = 5 * 60 * 1000;

/** Stable query keys for every cached reference list. */
export const referenceQueryKeys = {
  cropTypes: ['reference', 'cropTypes'] as const,
  cropSubTypes: ['reference', 'cropSubTypes'] as const,
  cropSubTypesByCropType: (cropTypeId: number | undefined) =>
    ['reference', 'cropSubTypes', 'byCropType', cropTypeId ?? null] as const,
  regions: ['reference', 'regions'] as const,
  elementRankings: (params?: ElementRankingParams) =>
    ['reference', 'elementRankings', params ?? null] as const,
  labs: ['reference', 'labs'] as const,
};

/** Common options accepted by the reference hooks. */
interface ReferenceQueryOptions {
  /** Set false to hold the fetch until a dependency is ready (default true). */
  enabled?: boolean;
}

/** Optional server-side sort/search for the element-rankings list. */
interface ElementRankingParams {
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/** All crop types (cached). */
export function useCropTypes(options: ReferenceQueryOptions = {}) {
  return useQuery({
    queryKey: referenceQueryKeys.cropTypes,
    queryFn: async () => (await cropTypesService.getAll()).data ?? [],
    staleTime: REFERENCE_DATA_STALE_TIME,
    enabled: options.enabled ?? true,
  });
}

/** All crop sub-types across every crop type (cached). */
export function useCropSubTypes(options: ReferenceQueryOptions = {}) {
  return useQuery({
    queryKey: referenceQueryKeys.cropSubTypes,
    queryFn: async () => (await cropSubTypesService.getAll()).data ?? [],
    staleTime: REFERENCE_DATA_STALE_TIME,
    enabled: options.enabled ?? true,
  });
}

/**
 * Crop sub-types for a single parent crop type (cached per crop type).
 * Disabled until a `cropTypeId` is provided.
 */
export function useCropSubTypesByCropType(
  cropTypeId: number | undefined,
  options: ReferenceQueryOptions = {},
) {
  return useQuery({
    queryKey: referenceQueryKeys.cropSubTypesByCropType(cropTypeId),
    queryFn: async () =>
      (await cropSubTypesService.getByCropType(cropTypeId as number)).data ?? [],
    staleTime: REFERENCE_DATA_STALE_TIME,
    enabled: (options.enabled ?? true) && cropTypeId != null,
  });
}

/** All regions (cached). `regionService.getAll()` already returns the array. */
export function useRegions(options: ReferenceQueryOptions = {}) {
  return useQuery({
    queryKey: referenceQueryKeys.regions,
    queryFn: () => regionService.getAll(),
    staleTime: REFERENCE_DATA_STALE_TIME,
    enabled: options.enabled ?? true,
  });
}

/** Element rankings (cached). Optional sort/search becomes part of the key. */
export function useElementRankings(
  params?: ElementRankingParams,
  options: ReferenceQueryOptions = {},
) {
  return useQuery({
    queryKey: referenceQueryKeys.elementRankings(params),
    queryFn: async () => (await elementRankingService.getAll(params)).data ?? [],
    staleTime: REFERENCE_DATA_STALE_TIME,
    enabled: options.enabled ?? true,
  });
}

/** All labs (cached). `labsService.getAll()` fetches the first 500 for dropdowns. */
export function useLabs(options: ReferenceQueryOptions = {}) {
  return useQuery({
    queryKey: referenceQueryKeys.labs,
    queryFn: async () => (await labsService.getAll()).data ?? [],
    staleTime: REFERENCE_DATA_STALE_TIME,
    enabled: options.enabled ?? true,
  });
}
